import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useAuth } from './components/AuthContext';
import PrivateRoute from './components/PrivateRoute';
// Nothing works without a resume. Wrapped INSIDE PrivateRoute everywhere, so
// "are you signed in" is still answered first — see RequireResume for which
// two pages are deliberately exempt.
import RequireResume from './components/RequireResume';
import { API_BASE, fetchWithTimeout } from './apiSession';
import { ServerSlow, useServerHealth } from './components/ServerSlow';
import SiteNotice from './components/SiteNotice';

import Navigation from './components/NavigationPanel';
import Footer from './components/Footer';

// The one page loaded eagerly rather than on demand. assistalien.com resolves
// here for every new visitor, so it is the worst place in the product to spend
// an extra round trip waiting for a chunk. It costs about 2KB in the initial
// bundle — its icons and fonts are already there for the app shell — which is
// a good trade for the first thing anyone sees.
import LandingPage from './components/landingPage';
import 'bootstrap/dist/css/bootstrap.min.css';

import { ProfileDataProvider } from './context/ProfileDataContext';
import { OmniAgentProvider } from './context/OmniAgentContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConfirmProvider } from './context/ConfirmContext';
import AntigravitySidebar from './components/AntigravitySidebar/AntigravitySidebar';
import ChatShell from './components/Shell/ChatShell';
import { isShellPath } from './components/Shell/panelRoutes';

import { WelcomeNameGate } from './components/WelcomeName';
import { GettingStartedProvider } from './context/GettingStartedContext';
import GettingStartedBar from './components/GettingStarted/GettingStartedBar';
import { FeatureGateProvider, useFeatureGate } from './context/FeatureGateContext';
import useKeyboardViewport from './hooks/useKeyboardViewport';
// Asks the server who may open each admin page (admin, or a read-only helper)
// instead of comparing against one account (docs/TODO.md Part 0, A1). Static,
// because it decides whether the lazy admin page loads at all.
import AdminRoute from './components/BrainHub/AdminRoute';

// Every page is loaded on demand.
//
// These were all static imports, which meant one chunk containing every
// screen in the product: opening job search downloaded the rich-text editor,
// the PDF renderer and the admin console too, and none of it could paint
// until all of it had parsed. The heaviest of them are used by the fewest
// people — the three admin pages are reachable by a single uid.
//
// Route-level is the right granularity: a chunk boundary the router already
// enforces, so nothing renders a page it hasn't finished loading.
const ResumeHub           = lazy(() => import('./components/ResumeHub'));
const Account             = lazy(() => import('./components/Account'));
const Dashboard           = lazy(() => import('./components/Dashboard'));
const JobTracker          = lazy(() => import('./components/JobTracker/JobTracker'));
const InterviewCoachPage  = lazy(() => import('./components/Zaga/Zaga'));
const NotFound            = lazy(() => import('./components/NotFound'));
// The jobs screen: feed, search, saved and applied. /jobs used to be a second
// screen with its own ranking; see the note at the top of JobFeed.js for why
// there is one now.
const JobFeed             = lazy(() => import('./components/JobFeed/JobFeed'));

// Named exports — lazy() requires a module whose default is the component.

// Admin. One uid can reach these; everyone else should never pay for them.
const JobAgent            = lazy(() => import('./components/JobAgent/JobAgent'));
const ChatsPage           = lazy(() => import('./components/Chats/ChatsPage'));
const AboutYou            = lazy(() => import('./components/AboutYou/AboutYou'));
const BrainHub            = lazy(() => import('./components/BrainHub/BrainHub'));
const BrainHubOld         = lazy(() => import('./components/BrainHub/BrainHubOld'));
const SyncHistoryPage     = lazy(() => import('./components/BrainHub/SyncHistoryPage'));
const DatabaseBrowserPage = lazy(() => import('./components/BrainHub/DatabaseBrowserPage'));

// Auth and public pages.
const Login             = lazy(() => import('./components/Login'));
const Logout            = lazy(() => import('./components/logout'));
const Register          = lazy(() => import('./components/Register'));
const ForgotPassword    = lazy(() => import('./components/ForgetPassword'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicy'));
const PricingPage = lazy(() => import('./components/Pricing'));
const ExtensionPage = lazy(() => import('./components/ExtensionPage'));
const TermsOfServicePage = lazy(() => import('./components/TermsOfServices'));
const SecurityPage      = lazy(() => import('./components/security'));
const ExtensionPrivacyPage = lazy(() => import('./components/ExtensionPrivacy'));

// Shown while a page's chunk is downloading. It fills the pane rather than
// centring in the viewport, because the navigation is still on screen beside
// it — matching the app's existing loading screen so a slow chunk reads as
// the app working, not as a broken page.
const RouteFallback = () => (
  <div style={{
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    width: '100%'
  }}>
    <Spinner animation="border" role="status" variant="primary">
      <span className="visually-hidden">Loading…</span>
    </Spinner>
  </div>
);

const GatedRoute = ({ children, flag }) => {
  const { isFeatureEnabled, loading } = useFeatureGate();
  if (loading) return <RouteFallback />;
  return isFeatureEnabled(flag) ? children : <Navigate to="/dashboard" replace />;
};

// Where the footer does NOT belong. Everything else gets it.
//
// It used to be the other way round — an allow-list of four public routes,
// AND only when signed out. Two things were wrong with that. A signed-in
// person had no route to the privacy policy or the terms from anywhere in the
// product, which is the one place those links genuinely have to be reachable.
// And the landing page, excluded because it shipped its own inline copy, gave
// the site two different footers that disagreed about what it offered; that
// copy is gone and this one renders there too.
//
// Two kinds of page still opt out:
//   - IMMERSIVE_ROUTES below: they size themselves in viewport height and
//     manage their own inner scroll, so a footer after them either never
//     appears or adds a second scrollbar.
//   - the auth screens: a 400px sign-in form under a three-column footer
//     reads as a page that failed to load its content.
const NO_FOOTER_ROUTES = ['/login', '/logout', '/register', '/forgot-password'];

// Full-viewport app routes — no navbar padding, no footer, and (via
// isImmersive below) the main pane's own scroll is locked because these
// pages manage their own internal scroll region (e.g. a fixed toolbar +
// scrollable canvas/chat). /admin is not listed: the prefix match would take
// /admin/database and /admin/sync-history too, which are ordinary growing
// pages (locking the outer pane made the old admin page unscrollable, live
// 2026-08). The admin console itself, exactly /admin, has had its own frame
// with an inner scrolling column since the 2026-10 rebuild, so AppShell
// treats it as immersive by name (inAdminConsole).
// /job-agent belongs here for the same reason /design-studio does: it docks a
// conversation beside a scrolling pane, so it manages its own scroll region and
// its shell is sized in viewport height. Without it the outer pane grows, the
// 100%-height shell has no parent height to resolve against, and the docked
// chat collapses.
const IMMERSIVE_ROUTES = ['/design-studio', '/tracking', '/interview-coach', '/dashboard', '/job-agent', '/for-you', '/chats', '/about-you'];

// On a phone these pages put their own controls in the strip beside the
// hamburger (the fixed mobile bar is transparent and lets taps through), so
// the content column is not padded past it for them. See claudeTheme.css,
// "Design Studio on a phone".
const STRIP_OWNER_ROUTES = ['/design-studio'];


const AppShell = ({ currentUser, isMobile, backendSlow = false }) => {
  const { pathname } = useLocation();
  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('Resume updated');

  useEffect(() => {
    const handleRefresh = (e) => {
      // Name the section that changed. "Profile Synced Successfully" was shown
      // for every edit to every part of the resume, which told the user
      // something had happened but never what — so it read as noise and got
      // ignored, which is the opposite of what a confirmation is for.
      const type = e?.detail?.type;
      const label = type
        ? { EXPERIENCE: 'Experience', EDUCATION: 'Education', PROJECTS: 'Projects',
            SKILLS: 'Skills', CERTIFICATIONS: 'Certifications', SUMMARIES: 'Summary' }[type]
        : null;
      const verb = e?.detail?.action === 'ADD' ? 'added to'
        : e?.detail?.action === 'DELETE' ? 'removed from'
        : 'updated in';

      setToastText(label ? `${label} ${verb === 'updated in' ? 'updated' : verb.replace(' in', '')}` : 'Resume updated');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    };
    // Design changes apply straight to the preview with no card and no save
    // step, which makes them the easiest of all to miss — the page quietly
    // looks different and nothing says why.
    const handleDesign = (e) => {
      const { format, font, layoutSettings } = e.detail || {};
      const TEMPLATE_NAMES = { faangpath: 'Silicon', luxsleek: 'Aura', iiit: 'Ivy', tlc: 'Quant' };

      setToastText(
        format ? `Template changed to ${TEMPLATE_NAMES[format] || format}`
        : font ? `Font changed to ${font === 'default' ? 'the default' : font}`
        : layoutSettings && Object.keys(layoutSettings).length ? 'Layout updated'
        : 'Design updated'
      );
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    };

    window.addEventListener('refresh-profile', handleRefresh);
    window.addEventListener('design-studio-update', handleDesign);
    return () => {
      window.removeEventListener('refresh-profile', handleRefresh);
      window.removeEventListener('design-studio-update', handleDesign);
    };
  }, []);

  // The admin console (BrainHub) is a whole screen of its own: its sidebar,
  // its top bar (also on a phone) and its own scrolling column. So it is
  // immersive, draws its own strip, and gets none of the app's chrome.
  const inAdminConsole = pathname === '/admin' || pathname === '/admin/';
  const isImmersive = IMMERSIVE_ROUTES.some(r => pathname === r || pathname.startsWith(r + '/')) || inAdminConsole;
  // The chat-first shell: one conversation, the page beside it. Phones keep
  // today's screens until the phone sheet (plan phase 6).
  const inShell = Boolean(currentUser) && !isMobile && isShellPath(pathname);
  const ownsStrip   = STRIP_OWNER_ROUTES.some(r => pathname === r || pathname.startsWith(r + '/')) || pathname === '/for-you' || pathname.startsWith('/for-you/') || pathname === '/interview-coach' || pathname.startsWith('/interview-coach/') || pathname === '/account' || inAdminConsole;
  const matches = (list) => list.some(r => pathname === r || pathname.startsWith(r + '/'));
  const showFooter  = !isImmersive && !matches(NO_FOOTER_ROUTES);

  // On a phone, an immersive page's shell is as tall as what is actually
  // visible — which the on-screen keyboard shortens — and moves down with
  // the page when iOS scrolls it to reach a field. See the hook.
  const followsKeyboard = isMobile && isImmersive;
  useKeyboardViewport(followsKeyboard);

  // Every page, by URL. Rendered in one of two places below.
  const routes = (
    <Routes>
      <Route path="/design-studio"              element={<PrivateRoute><ResumeHub /></PrivateRoute>} />
      {/* Import Resume now lives inside Design Studio itself (an
          onboarding action, not its own page) — old links/bookmarks to
          /import still work, they just land in the builder with the
          import modal opened automatically. */}
      <Route path="/import"           element={<Navigate to="/design-studio?import=1" replace />} />
      <Route path="/account"          element={<PrivateRoute><Account /></PrivateRoute>} />
      {/* The answer bank. The assistant sidebar routes by page (see the
          agent registry), so opening this hands the conversation to the
          answer agent — which is why the page carries no chat of its own. */}
      <Route path="/job-agent"        element={<PrivateRoute><RequireResume><JobAgent /></RequireResume></PrivateRoute>} />
      {/* /profile was the account page under a name that also meant the
          résumé profile — the assistant even treated it as a resume
          surface. Old links and bookmarks still land somewhere real. */}
      <Route path="/profile"          element={<Navigate to="/account" replace />} />

      <Route path="/dashboard"        element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      {/* Every conversation, and a search across them. No resume needed: the
          chat is always open. */}
      <Route path="/chats"            element={<PrivateRoute><ChatsPage /></PrivateRoute>} />
      {/* Everything the product keeps about the person, one list. No resume
          needed: the setup that fills it starts with one. */}
      <Route path="/about-you"        element={<PrivateRoute><AboutYou /></PrivateRoute>} />
      <Route path="/tracking"         element={<PrivateRoute><RequireResume><GatedRoute flag="JOB_TRACKER"><JobTracker /></GatedRoute></RequireResume></PrivateRoute>} />
      <Route path="/interview-coach" element={<PrivateRoute><RequireResume><InterviewCoachPage /></RequireResume></PrivateRoute>} />
      {/* The coach's old name; links and bookmarks keep working. */}
      <Route path="/zaga"             element={<Navigate to="/interview-coach" replace />} />
      <Route path="/for-you"          element={<PrivateRoute><JobFeed /></PrivateRoute>} />
      {/* Kept as a redirect rather than removed: /jobs is in people's
          history and bookmarks, and a 404 on a URL that worked yesterday
          is a worse answer than the screen that replaced it. */}
      <Route path="/jobs"             element={<Navigate to="/for-you" replace />} />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <AdminRoute allow={['admin', 'viewer']}><BrainHub /></AdminRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/sync-history"
        element={
          <PrivateRoute>
            <AdminRoute allow={['admin', 'viewer']}><SyncHistoryPage /></AdminRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/database"
        element={
          <PrivateRoute>
            <AdminRoute allow={['admin']}><DatabaseBrowserPage /></AdminRoute>
          </PrivateRoute>
        }
      />
      {/* The admin page before the console, for admins while it settles in. */}
      <Route
        path="/admin/old"
        element={
          <PrivateRoute>
            <AdminRoute allow={['admin']}><BrainHubOld /></AdminRoute>
          </PrivateRoute>
        }
      />

      {/* Public auth */}
      <Route path="/login"           element={!currentUser ? <Login />         : <Navigate to="/dashboard" replace />} />
      <Route path="/logout"          element={<Logout />} />
      <Route path="/register"        element={!currentUser ? <Register />       : <Navigate to="/dashboard" replace />} />
      <Route path="/forgot-password" element={!currentUser ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />

      {/* Public legal & info pages */}
      <Route path="/pricing"          element={<PricingPage />} />
      {/* The old What we do page: its section is on the landing page now (2026-11-02). */}
      <Route path="/what-we-do"       element={<Navigate to="/#what-we-do" replace />} />
      <Route path="/extension"        element={<ExtensionPage />} />
      <Route path="/privacy"          element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/security"         element={<SecurityPage />} />
      <Route path="/extension-privacy" element={<ExtensionPrivacyPage />} />
      <Route path="/landing"          element={<LandingPage />} />

      {/* The root URL renders the landing page rather than redirecting to
          /landing. assistalien.com is the address people are given, type
          and link to, so it should be the page itself — a bounce costs a
          round trip before anything paints and leaves visitors on a URL
          they were never sent. A signed-in user still goes straight to
          their dashboard; /landing remains a valid address for both. */}
      <Route
        path="/"
        element={currentUser ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />
      <Route
        path="*"
        /* A real 404 for everyone. The silent bounce (to the dashboard, and
           for visitors to /landing) made a typo'd URL look like a successful
           navigation. Redirects that are meant (/jobs, /zaga, /profile,
           /import) are routes of their own above. */
        element={<NotFound />}
      />
    </Routes>
  );

  return (
    <>
     {/* data-no-scroll: the frame never scrolls itself (windowLock.js). */}
     <div data-no-scroll="" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        height: isMobile && !isImmersive ? 'auto' : (followsKeyboard ? 'var(--app-height, 100dvh)' : '100dvh'),
        minHeight: followsKeyboard ? 0 : '100dvh',
        overflow: isMobile && !isImmersive ? 'visible' : 'hidden', 
        width: '100%', 
        position: 'relative',
        top: followsKeyboard ? 'var(--app-top, 0px)' : undefined,
      }}>
        
        {/* Global Success Toast. Fixed and only faded out, never parked
            below the frame: at bottom -100px it gave the frame (overflow
            hidden) 150px of hidden room, and any focus near the bottom slid
            the whole app up into it, leaving an empty band until a reload
            (the founder, 2026-11-01). */}
        <div role="status" aria-live="polite" style={{
          position: 'fixed',
          bottom: '40px',
          left: '50%',
          transform: showToast ? 'translate(-50%, 0)' : 'translate(-50%, 16px)',
          visibility: showToast ? 'visible' : 'hidden',
          backgroundColor: '#0f172a',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 9999,
          fontWeight: '500',
          fontSize: '0.9rem',
          transition: showToast ? 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)' : 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s linear 0.4s',
          pointerEvents: 'none',
          opacity: showToast ? 1 : 0
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {toastText}
        </div>
        {/* Navigation Sidebar & Public Header */}
        {!inAdminConsole && <Navigation ownsStrip={ownsStrip} isPublic={!currentUser} />}

        {/* Left Pane: AI Sidebar (slides out from Navigation). Not in the
            shell, which has the chat on screen already. */}
        {currentUser && !inShell && !inAdminConsole && <AntigravitySidebar />}

        {/* Center Pane: Main App Content */}
        <div
          className="app-main-pane"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: isImmersive ? 'hidden' : (isMobile ? 'visible' : 'auto'),
            backgroundColor: 'transparent', zIndex: 0,
            position: 'relative',
            height: isImmersive ? '100%' : 'auto',
            minHeight: 0,
            // Clears the fixed mobile top bar. Reads the same custom property
            // NavigationPanel sizes the bar with, so the two cannot drift —
            // they were separately hard-coded to 60px before.
            paddingTop: isMobile && !ownsStrip ? 'var(--mobile-header-h)' : '0'
          }}
        >
        {backendSlow && isMobile && !ownsStrip && <ServerSlow verdict={backendSlow} />}
        {/* The first-run steps, on every page of the app until the person
            is ready to apply (founder, 2026-09-28). A phone page that draws
            its own strip shows them under it itself (GettingStartedBar). */}
        {/* The site-wide notice an admin sets in the console (which shows it itself). */}
        {!inAdminConsole && <SiteNotice />}
        {currentUser && !inAdminConsole && <GettingStartedBar inShell={inShell} isMobile={isMobile} ownsStrip={ownsStrip} />}

        <div
          className="app-main-content"
          data-no-scroll=""
          style={{
            flexGrow: 1,
            // A page keeps its own height; the pane scrolls it and the footer
            // follows it. Only an immersive page, which scrolls inside itself,
            // may be squeezed to the pane. With min-height 0 on every page, a
            // desktop screen squeezed the landing page into the space left
            // above the footer: 508px of an 8,436px page, with its own inner
            // scrollbar (2026-09-24). A phone never showed it: there the frame
            // has no fixed height.
            flexShrink: isImmersive ? 1 : 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: isImmersive ? 'hidden' : 'visible',
            padding: isImmersive ? '0' : '0.0rem',
            width: '100%',
            height: isImmersive ? '100%' : 'auto',
            minHeight: isImmersive ? 0 : undefined,
          }}
        >
        {/* Inside the layout, not around it: the navigation and sidebar are
            already loaded, so they should stay on screen while a page's chunk
            arrives. A boundary above this would blank the whole app on every
            navigation and feel worse than the single bundle it replaced.

            Signed in, wider than a phone, on the home or a workspace page:
            the one chat, with the page for the URL beside it in its own
            boundary (Shell/ChatShell.js, 2026-09-27). Everywhere else, the
            page as it was. */}
        {inShell ? (
          <ChatShell fallback={<RouteFallback />}>{routes}</ChatShell>
        ) : (
          <Suspense fallback={<RouteFallback />}>{routes}</Suspense>
        )}
        </div>

        {showFooter && <Footer />}
        {backendSlow && isMobile && ownsStrip && <ServerSlow verdict={backendSlow} atFoot />}
      </div>
      </div>
    </>
  );
};

const App = () => {
  const { currentUser, isLoading } = useAuth();
  // Inclusive, to agree with the stylesheets: NavigationPanel swaps the
  // sidebar for the hamburger drawer at max-width: 768px, and an iPad held
  // upright is exactly 768 wide. With "< 768" that one width got the drawer's
  // hamburger and no room cleared for it.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Not a gate any more. The result of this check was always discarded (the
  // finally set ready=true either way), so all it did was hold the whole app
  // on a spinner for as long as one untimed request took — on a slow server,
  // 1-20 seconds before the next gate went blank. Now it only decides
  // whether to show the "server is slow" line, and it has a deadline.
  // The latest health verdict (components/ServerSlow.js): null when the
  // server answered in time, else how slow or how down. Checked again every
  // minute while the tab is open.
  const backendSlow = useServerHealth(currentUser, {
    fetcher: (deadlineMs) => fetchWithTimeout(`${API_BASE}/healthz`, { cache: 'no-store' }, deadlineMs),
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return (
      <ThemeProvider>
        <div className="loading-screen">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        {/* Outside the data providers: a confirmation is chrome, not state, and
            anything inside here may need to ask before it destroys something. */}
        <ConfirmProvider>
        <FeatureGateProvider>
          <ProfileDataProvider>
            <OmniAgentProvider>
              {/* Asked once, before anything else, and only of signed-in
                  users who have not answered it. Inside the router because it
                  sits over whatever route they arrived on; outside AppShell so
                  the navigation is not visible behind a question that has to
                  be answered. */}
              {backendSlow && !isMobile && <ServerSlow verdict={backendSlow} />}
              <WelcomeNameGate>
                <GettingStartedProvider>
                  <AppShell currentUser={currentUser} isMobile={isMobile} backendSlow={backendSlow} />
                </GettingStartedProvider>
              </WelcomeNameGate>
            </OmniAgentProvider>
          </ProfileDataProvider>
        </FeatureGateProvider>
        </ConfirmProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
